import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

// "Explore What's Local" — informational marketing groupings only.
// These cards do NOT map to or alter the app's actual categories.
// All copy below is supplied final content (static; no runtime generation).
const CATEGORIES = [
  {
    label: "Vegetables & Fruits",
    img: "https://images.unsplash.com/photo-1591586116988-62fe65164f8d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000&ixlib=rb-4.1.0",
    alt: "Fresh seasonal vegetables including cauliflower, broccoli and radishes at a farm market stall",
    intro:
      "From orchard-fresh apples and cherries to seasonal vegetables, berries, and leafy greens, British Columbia offers an incredible variety of locally grown produce. Exploring local farms gives you an opportunity to discover what's in season, learn where your food comes from, and connect with the people growing it.",
    discover:
      "Apples, cherries, peaches, berries, tomatoes, carrots, potatoes, lettuce, squash, and other seasonal produce.",
    buyers: [
      "Discover seasonal varieties and farm-direct produce.",
      "Learn about growing methods and harvesting seasons.",
      "Explore nutritious ingredients for everyday meals.",
    ],
    producers: [
      "Greater visibility for independent farms and orchards.",
      "Opportunities to connect directly with customers.",
      "More ways to promote seasonal harvests and specialty crops.",
    ],
    community:
      "Choosing locally grown produce helps support the farmers, orchards, and agricultural businesses that contribute to British Columbia's food economy.",
    links: [
      { label: "Buy BC — What's in Season", url: "https://buybc.gov.bc.ca/eat-local/whats-in-season/" },
      { label: "Health Canada — Eat Vegetables and Fruits", url: "https://www.canada.ca/en/health-canada/services/food-guide/explore/healthy-eating-recommendations/eat-variety/eat-vegetables-fruits.html" },
    ],
  },
  {
    label: "Eggs & Honey",
    img: "https://images.unsplash.com/photo-1783185572059-6a23c601441c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000&ixlib=rb-4.1.0",
    alt: "Rustic roadside farm stand selling fresh eggs and local honey",
    intro:
      "Discover the people behind locally produced eggs and honey. From small egg farms to independent beekeepers, these producers offer opportunities to explore regional products, learn about agricultural practices, and understand the work involved in bringing everyday foods to our tables.",
    discover:
      "Farm-fresh eggs, half-dozen and dozen cartons, local honey, seasonal honey varieties, and specialty farm products.",
    buyers: [
      "Learn about egg production and beekeeping practices.",
      "Discover local honey varieties and farm-direct offerings.",
      "Connect with producers and ask questions about their products.",
    ],
    producers: [
      "Increased visibility for small egg farms and beekeepers.",
      "Opportunities to develop repeat customer relationships.",
      "Greater awareness of their products and farming practices.",
    ],
    community:
      "Supporting independent egg producers and beekeepers helps strengthen connections between consumers and the people involved in regional food production.",
    links: [
      { label: "Buy BC — About Buy BC", url: "https://buybc.gov.bc.ca/about/about-buybc/" },
      { label: "Health Canada — Eat Protein Foods", url: "https://www.canada.ca/en/health-canada/services/food-guide/explore/healthy-eating-recommendations/eat-variety/eat-proteins.html" },
    ],
  },
  {
    label: "Meat & Dairy",
    img: "https://images.pexels.com/photos/5732759/pexels-photo-5732759.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=750&w=1000",
    alt: "Rustic wooden board with artisan cheese, camembert, goat cheese and cured meats",
    intro:
      "Explore locally raised meats and dairy products while getting to know the farms and businesses behind them. From specialty cuts and poultry to cheese and yogurt, local producers offer opportunities to learn about sourcing, farming practices, and the care involved in producing food.",
    discover:
      "Beef, chicken, pork, lamb, milk, cheese, yogurt, and specialty dairy products.",
    buyers: [
      "Ask producers about sourcing and animal care practices.",
      "Discover specialty cuts and locally produced dairy products.",
      "Better understand where products come from.",
    ],
    producers: [
      "Opportunities to build direct customer relationships.",
      "Greater visibility for independent livestock and dairy businesses.",
      "More opportunities to explain production methods and product differences.",
    ],
    community:
      "Supporting local livestock farms and dairy producers helps maintain regional agricultural businesses and connections between consumers and food producers.",
    links: [
      { label: "Health Canada — Eat Protein Foods", url: "https://www.canada.ca/en/health-canada/services/food-guide/explore/healthy-eating-recommendations/eat-variety/eat-proteins.html" },
      { label: "Buy BC — What's From B.C.?", url: "https://buybc.gov.bc.ca/about/whats-from-bc/" },
    ],
  },
  {
    label: "Flowers & Plants",
    img: "https://images.unsplash.com/photo-1685613858397-64f79a0f3603?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000&ixlib=rb-4.1.0",
    alt: "Vibrant assortment of fresh-cut flowers and bouquets at a local flower market",
    intro:
      "Bring a little local beauty into your home or garden. Discover flower growers, nurseries, and plant producers offering seasonal bouquets, seedlings, herbs, and garden plants. Buying directly from growers also provides opportunities to learn which varieties may suit your garden and growing conditions.",
    discover:
      "Fresh-cut flowers, bouquets, seedlings, potted herbs, nursery plants, shrubs, perennials, and fruit trees.",
    buyers: [
      "Explore seasonal flowers and locally grown plants.",
      "Ask growers for planting and care advice.",
      "Discover varieties suited to regional growing conditions.",
    ],
    producers: [
      "Greater visibility for flower farms and nurseries.",
      "Opportunities to reach gardeners and seasonal shoppers.",
      "Stronger relationships with returning customers.",
    ],
    community:
      "Choosing local flowers and plants supports independent growers, nurseries, and the horticultural businesses that contribute to vibrant communities.",
    links: [
      { label: "Buy BC — About Buy BC", url: "https://buybc.gov.bc.ca/about/about-buybc/" },
    ],
  },
  {
    label: "Artisan Foods",
    img: "https://images.pexels.com/photos/14575525/pexels-photo-14575525.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=750&w=1000",
    alt: "Artisanal rustic sourdough breads and loaves displayed at a farmers market",
    intro:
      "Discover the creativity and craftsmanship behind locally made specialty foods. From freshly baked bread to preserves, mushrooms, and maple syrup, independent producers offer distinctive products and an opportunity to learn about the ingredients, techniques, and traditions behind them.",
    discover:
      "Artisan bread, pastries, jams, pickles, fermented foods, culinary mushrooms, maple syrup, and other specialty foods.",
    buyers: [
      "Discover distinctive small-batch and specialty products.",
      "Learn about ingredients and preparation methods.",
      "Explore regional flavours and food traditions.",
    ],
    producers: [
      "Opportunities to share the stories behind their products.",
      "Greater visibility for independent food businesses.",
      "Direct connections with customers interested in specialty foods.",
    ],
    community:
      "Supporting independent food makers helps encourage local entrepreneurship, regional food traditions, and a diverse food economy.",
    links: [
      { label: "Buy BC — Seven Ways to Support B.C.'s Food and Beverage Makers", url: "https://buybc.gov.bc.ca/blog/7-ways-to-support-b-c-s-food-and-beverage-makers/" },
      { label: "Buy BC — What's From B.C.?", url: "https://buybc.gov.bc.ca/about/whats-from-bc/" },
    ],
  },
  {
    label: "Wineries & Craft Beverages",
    img: "https://images.pexels.com/photos/37555750/pexels-photo-37555750.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=750&w=1000",
    alt: "Assorted local wine bottles on display at a winery tasting room",
    intro:
      "British Columbia is home to regional wineries, cideries, and craft beverage producers offering distinctive products shaped by local ingredients, traditions, and craftsmanship. Discover the businesses behind these beverages and learn more about the people and production methods that make each producer unique.",
    discover:
      "Local wines, craft beers, ciders, seasonal releases, and regional specialty beverages.",
    buyers: [
      "Discover independent regional beverage producers.",
      "Learn about ingredients and production methods.",
      "Explore regional beverage traditions and producer stories.",
    ],
    producers: [
      "Greater visibility for independent wineries, breweries, and cideries.",
      "Opportunities to introduce customers to regional products.",
      "Increased awareness of their businesses and production methods.",
    ],
    community:
      "Supporting regional beverage producers helps strengthen connections with local agricultural and beverage businesses and contributes to the diversity of British Columbia's food and beverage sector.",
    responsible:
      "Alcohol is intended for adults of legal drinking age. Please enjoy responsibly.",
    links: [
      { label: "Buy BC — About Buy BC", url: "https://buybc.gov.bc.ca/about/about-buybc/" },
      { label: "Buy BC — Seven Ways to Support B.C.'s Food and Beverage Makers", url: "https://buybc.gov.bc.ca/blog/7-ways-to-support-b-c-s-food-and-beverage-makers/" },
    ],
  },
];

const SectionHeading = ({ children }) => (
  <h4 className="mt-6 mb-2 font-sans font-extrabold text-base text-[#1C241B]">{children}</h4>
);

const Bullets = ({ items }) => (
  <ul className="list-disc pl-5 space-y-1.5 text-[#2b332a] text-sm leading-relaxed">
    {items.map((i) => <li key={i}>{i}</li>)}
  </ul>
);

// One reusable modal, used for all six cards.
const CategoryModal = ({ c }) => (
  <Dialog>
    <DialogTrigger asChild>
      <button
        type="button"
        data-testid={`explore-card-${c.label.replace(/[^a-zA-Z]/g, "").toLowerCase()}`}
        className="group block w-full text-left rounded-2xl overflow-hidden bg-white border border-[#D8E0D5] shadow-sm hover:shadow-xl transition-shadow duration-300 focus:outline-none focus-visible:ring-4 focus-visible:ring-[#4A6B41]/30"
      >
        <div className="overflow-hidden">
          <img
            src={c.img}
            alt={c.alt}
            loading="lazy"
            decoding="async"
            className="w-full h-56 object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <span className="block px-5 py-4 font-sans font-bold text-lg text-[#1C241B]">
          {c.label}
        </span>
      </button>
    </DialogTrigger>

    <DialogContent className="max-w-[720px] max-h-[85vh] overflow-y-auto bg-[#F9F6F0] p-0">
      <img
        src={c.img}
        alt={c.alt}
        className="w-full h-52 sm:h-60 object-cover rounded-t-lg"
      />
      <div className="px-6 pb-6 pt-5">
        <DialogTitle className="font-sans font-extrabold text-2xl text-[#1C241B]">
          {c.label}
        </DialogTitle>
        <DialogDescription className="mt-3 text-[#2b332a] text-sm sm:text-base leading-relaxed">
          {c.intro}
        </DialogDescription>

        <SectionHeading>What You Can Discover</SectionHeading>
        <p className="text-[#2b332a] text-sm leading-relaxed">{c.discover}</p>

        <SectionHeading>Benefits for Buyers</SectionHeading>
        <Bullets items={c.buyers} />

        <SectionHeading>Benefits for Local Producers</SectionHeading>
        <Bullets items={c.producers} />

        <SectionHeading>Supporting Our Communities</SectionHeading>
        <p className="text-[#2b332a] text-sm leading-relaxed">{c.community}</p>

        {c.responsible && (
          <>
            <SectionHeading>Responsible Consumption</SectionHeading>
            <p className="text-[#2b332a] text-sm leading-relaxed">{c.responsible}</p>
          </>
        )}

        <SectionHeading>Learn More</SectionHeading>
        <ul className="space-y-1.5">
          {c.links.map((l) => (
            <li key={l.url}>
              <a
                href={l.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-[#4A6B41] underline underline-offset-2 hover:text-[#2F4829] break-words"
              >
                {l.label}
              </a>
            </li>
          ))}
        </ul>
      </div>
    </DialogContent>
  </Dialog>
);

export const ExploreLocal = () => {
  return (
    <section
      id="explore-local"
      data-testid="explore-local-section"
      className="px-4 sm:px-6 lg:px-12 py-20 md:py-28 bg-[#F9F6F0]"
    >
      <div className="mx-auto max-w-6xl">
        <div className="text-center max-w-3xl mx-auto">
          <h2 className="font-sans font-extrabold text-3xl sm:text-4xl lg:text-5xl text-[#1C241B] tracking-tight">
            Explore What's Local
          </h2>
          <p className="mt-4 font-sans text-base sm:text-lg text-[#5A6757] leading-relaxed">
            From farm-fresh produce and locally raised meats to artisan foods,
            flowers, and wineries — discover what's grown, raised, and made near
            you.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {CATEGORIES.map((c) => (
            <CategoryModal key={c.label} c={c} />
          ))}
        </div>

        <div className="mt-12 text-center max-w-2xl mx-auto">
          <p className="font-sans font-extrabold text-xl text-[#4A6B41]">
            And so much more...
          </p>
          <p className="mt-2 font-sans text-base text-[#5A6757] leading-relaxed">
            Explore mushrooms, herbs and spices, U-Pick farms, farmers markets,
            farm experiences, and other unique local discoveries.
          </p>
        </div>
      </div>
    </section>
  );
};
